import {
  CardViewEnum,
  isMultileg,
  UnifiedLiquidityEnum,
  useCallbackRef,
  useMarketAccountsContext,
  useSecuritiesContext,
  useSecurity,
  useUnifiedLiquidityContext,
  type MarketAccount,
  type MarketDataCardConfig,
  type MarketDataCardUnifiedLiquidityMarket,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { getSymbolAndMarketsNoUnifiedLiquidity } from '../../utils/symbolAndMarketsNoUnifiedLiquidity';
import { filterMarketAccounts, filterMarkets } from '../MarketAccountSelectionList/utils';
import { removeAnyMultipleSelectionsPerMarket } from '../MarketSelector/utils/OrderMarketsField';
import { getUnifiedLiquiditySelectionsForMarketAccounts } from '../MarketSelector/utils/unifiedLiquidity';
import { toMultilegSelectedMarketAccounts, useMultilegLegMarketAccountsEditing } from './multilegLegMarketAccounts';
import {
  useMarketDataCardMarketSelector,
  useMarketSelectorDarkSelectionGuards,
  type PersistMarketDataCardMarketSelection,
} from './useMarketDataCardMarketSelector';
import { useMarketDataCardSymbolChangeSelections } from './useMarketDataCardSymbolChangeSelections';

interface SettingsForm {
  symbol: string | undefined;
  title: string;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  unifiedLiquidityMarketSymbols: MarketDataCardUnifiedLiquidityMarket[];
  selectedAggregation: string | undefined;
  view: CardViewEnum;
}

interface UseMarketDataCardSettingsParams {
  config: MarketDataCardConfig;
}

/**
 * This hook houses a bunch of business logic for the settings available on the Market Data Card Settings slide
 * The logic is put into this hook so that it can be more easily unit tested
 */
export const useMarketDataCardSettings = ({ config }: UseMarketDataCardSettingsParams) => {
  const { useNewMarketSelector } = useTradingSettings();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const getSymbolChangeSelections = useMarketDataCardSymbolChangeSelections();
  const { isMarketDark, isMarketAccountDark, getOnlyDarkOrNoDarkSelections } = useMarketSelectorDarkSelectionGuards();

  const [openMarketAccountSelector, setOpenMarketAccountSelector] = useState(false);
  const [ulEnabled, setULEnabled] = useState(config.unifiedLiquidity === UnifiedLiquidityEnum.Enabled);
  const [enableShowBBO, setShowBBO] = useState(config.alwaysDisplayBestBidOffer === true);
  const hasLoadedMarketAccountMaps = marketAccountsByName.size > 0 || marketAccountsByMarket.size > 0;

  const [form, setForm] = useState<SettingsForm>({
    symbol: config.symbol,
    title: config.title || '',
    selectedMarkets: hasLoadedMarketAccountMaps
      ? filterMarkets(config.selectedMarkets, marketAccountsByMarket)
      : config.selectedMarkets,
    selectedMarketAccounts: hasLoadedMarketAccountMaps
      ? filterMarketAccounts(
          config.selectedMarketAccounts,
          config.selectedMarkets,
          marketAccountsByMarket,
          marketAccountsByName
        )
      : config.selectedMarketAccounts,
    // If we for example disable the new market selector due to some problem, we make sure we dont use this config for anything
    selectedAggregation: useNewMarketSelector ? config.selectedAggregation : undefined,
    view: config.view || CardViewEnum.NORMAL,
    unifiedLiquidityMarketSymbols: config.unifiedLiquidityMarketSymbols ?? [],
  });

  const security = useSecurity(form.symbol);
  const { legMarketAccounts, onLegMarketAccountsChange } = useMultilegLegMarketAccountsEditing({ security });

  const updateForm = useCallback((update: Partial<SettingsForm>) => {
    setForm(prev => ({ ...prev, ...update }));
  }, []);

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      const ulResponse = symbol ? unifiedLiquidityTokenBySymbol?.get(symbol) : undefined;
      // Try to keep the UL setting. If UL isn't applicable to the new symbol however (no ulResponse), make sure it is set to false.
      const newULEnabled = ulEnabled && !!ulResponse;
      setULEnabled(newULEnabled);

      const selections = getSymbolChangeSelections(symbol, newULEnabled);
      if (!selections) {
        return;
      }

      const usesStructuredUnifiedLiquidityMarkets = newULEnabled && !isMultileg(selections.security);
      const unifiedLiquidityMarketSymbols = usesStructuredUnifiedLiquidityMarkets
        ? getUnifiedLiquiditySelectionsForMarketAccounts({
            symbol,
            selectedMarketAccounts: selections.selectedMarketAccounts,
            marketAccountsByName,
            securitiesBySymbol,
            unifiedLiquidityTokenBySymbol,
          })
        : [];

      updateForm({
        symbol,
        selectedMarkets: selections.selectedMarkets,
        selectedMarketAccounts: selections.selectedMarketAccounts,
        unifiedLiquidityMarketSymbols,
        selectedAggregation: selections.selectedAggregation,
      });
    },
    [
      marketAccountsByName,
      securitiesBySymbol,
      ulEnabled,
      unifiedLiquidityTokenBySymbol,
      getSymbolChangeSelections,
      updateForm,
    ]
  );

  const handleMarketAccountsChange = useCallback(
    (newSelectedMarketAccounts: string[]) => {
      // You can only have dark or non-dark selected. Would be nice to build support for this kind of situation into useMarketSelector.
      const newSelections = getOnlyDarkOrNoDarkSelections(isMarketAccountDark(newSelectedMarketAccounts.at(-1)), {
        marketAccounts: newSelectedMarketAccounts,
        markets: form.selectedMarkets,
      });

      updateForm({ selectedMarketAccounts: newSelections.marketAccounts, selectedMarkets: newSelections.markets });
    },
    [updateForm, getOnlyDarkOrNoDarkSelections, isMarketAccountDark, form.selectedMarkets]
  );

  const handleMarketsChange = useCallback(
    (newSelectedMarkets: string[]) => {
      // You can only have dark or non-dark selected. Would be nice to build support for this kind of situation into useMarketSelector.
      const newSelections = getOnlyDarkOrNoDarkSelections(isMarketDark(newSelectedMarkets.at(-1)), {
        marketAccounts: form.selectedMarketAccounts,
        markets: newSelectedMarkets,
      });

      updateForm({ selectedMarketAccounts: newSelections.marketAccounts, selectedMarkets: newSelections.markets });
    },
    [updateForm, getOnlyDarkOrNoDarkSelections, isMarketDark, form.selectedMarketAccounts]
  );

  const persistMarketSelection = useCallback(
    (update: PersistMarketDataCardMarketSelection) => {
      updateForm({
        selectedAggregation: update.selectedAggregation,
        selectedMarkets: update.selectedMarkets,
        selectedMarketAccounts: update.selectedMarketAccounts,
        ...(update.unifiedLiquidityMarketSymbols && {
          unifiedLiquidityMarketSymbols: update.unifiedLiquidityMarketSymbols,
        }),
      });
    },
    [updateForm]
  );
  const { marketSelector, oldMarketSelectorItems, marketSelectorItems } = useMarketDataCardMarketSelector({
    security,
    ulEnabled,
    selectedMarkets: form.selectedMarkets,
    selectedMarketAccounts: form.selectedMarketAccounts,
    selectedAggregation: form.selectedAggregation,
    onPersistMarketSelection: persistMarketSelection,
  });
  /* End - new market selector specifics */

  const handleChangeUnifiedLiquidity = useCallbackRef((unifiedLiquidityEnabled: boolean) => {
    if (!security) {
      return;
    }

    setULEnabled(unifiedLiquidityEnabled);
    // Force-open the market selector when enabling UL
    if (unifiedLiquidityEnabled) {
      setOpenMarketAccountSelector(true);
      updateForm({
        unifiedLiquidityMarketSymbols: getUnifiedLiquiditySelectionsForMarketAccounts({
          symbol: security.Symbol,
          selectedMarketAccounts: form.selectedMarketAccounts,
          marketAccountsByName,
          securitiesBySymbol,
          unifiedLiquidityTokenBySymbol,
        }),
      });
    }

    if (isMultileg(security)) {
      // Might be a bit confusing but it doesn't matter if we're enabling or disabling UL for ML - we can always set this and it is correct.
      updateForm({
        selectedMarkets: [],
        selectedMarketAccounts: [MULTILEG_MARKET_ACCOUNT],
        unifiedLiquidityMarketSymbols: [],
      });
      return;
    }

    // Non-multileg below. Only case that we need to look at is disabling UL.
    if (!unifiedLiquidityEnabled) {
      const hasLegacySelections = form.selectedMarkets.length > 0 || form.selectedMarketAccounts.length > 0;
      // If UL was disabled, and we are NOT a multileg market data card, then we have to remove the UL markets and accounts (if any)
      const nonULSelections = getSymbolAndMarketsNoUnifiedLiquidity({
        security,
        marketAccountsByName,
        marketSelections: hasLegacySelections ? form.selectedMarkets : [],
        marketAccountSelections: form.selectedMarketAccounts,
      });

      // There is also a situation where the client can have selected several market accounts per market. This is allowed for certain clients when UL is enabled.
      // If this is the case, then we also make sure to remove these. We use the current marketSelectorItems to decide which of the multiple valid
      // selections to stick with. We can still use the marketSelectorItems list _with UL_ (they haven't recomputed yet) since we just don't look at the UL items.
      if (hasMultipleMarketAccountsPerMarket(nonULSelections.marketAccounts, marketAccountsByName)) {
        nonULSelections.marketAccounts = removeAnyMultipleSelectionsPerMarket({
          selections: nonULSelections.marketAccounts,
          items: marketSelectorItems,
        });
      }

      updateForm({
        selectedMarkets: nonULSelections.markets,
        selectedMarketAccounts: nonULSelections.marketAccounts,
        unifiedLiquidityMarketSymbols: [],
      });
    }
  });

  const handleMultilegULMarketsChange = useCallbackRef((marketAccounts: string[]) => {
    handleMarketAccountsChange(toMultilegSelectedMarketAccounts(marketAccounts));
  });

  return {
    form,
    legMarketAccounts,
    ulEnabled,
    enableShowBBO,
    openMarketAccountSelector,
    marketSelector,
    oldMarketSelectorItems,
    marketSelectorItems,
    onChangeUnifiedLiquidity: handleChangeUnifiedLiquidity,
    setShowBBO,
    updateForm,
    onSymbolChange: handleSymbolChange,
    onMultilegULMarketsChange: handleMultilegULMarketsChange,
    onLegMarketAccountsChange,
    onMarketsChange: handleMarketsChange,
    onMarketAccountsChange: handleMarketAccountsChange,
  };
};

/** Simple utility that just checks if your array of market account names has any occurrence of 2+ accounts per market in it */
export function hasMultipleMarketAccountsPerMarket(
  marketAccountNames: string[],
  marketAccountsByName: Map<string, MarketAccount>
): boolean {
  const marketsFound = new Set();
  for (const marketAccountName of marketAccountNames) {
    const marketAccount = marketAccountsByName.get(marketAccountName);
    if (!marketAccount) {
      continue; // ignore
    }

    if (marketsFound.has(marketAccount.Market)) {
      return true;
    }

    marketsFound.add(marketAccount.Market);
  }

  return false;
}
